type LobbyStatusTone = "normal" | "ready" | "error";

type LobbyOptions = {
  routeCode: string;
  initialName: string;
  onCreate: (name: string) => void;
  onCreateAi: (name: string) => void;
  onJoin: (name: string, code: string) => void;
};

function requiredElement<T extends HTMLElement>(id: string, constructor: new () => T): T {
  const element = document.getElementById(id);
  if (!(element instanceof constructor)) {
    throw new Error("Lobby element #" + id + " is missing or has the wrong type.");
  }
  return element;
}

function normalizeRoomCode(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5);
}

export class LobbyView {
  private readonly root = requiredElement("lobby", HTMLElement);
  private readonly form = requiredElement("lobby-form", HTMLFormElement);
  private readonly nameInput = requiredElement("player-name", HTMLInputElement);
  private readonly codeInput = requiredElement("room-code", HTMLInputElement);
  private readonly createButton = requiredElement("create-pvp-button", HTMLButtonElement);
  private readonly aiButton = requiredElement("create-ai-button", HTMLButtonElement);
  private readonly joinButton = requiredElement("join-room-button", HTMLButtonElement);
  private readonly status = requiredElement("lobby-status", HTMLElement);
  private readonly statusMessage = requiredElement("lobby-status-message", HTMLElement);
  private readonly options: LobbyOptions;
  private connected = false;
  private busy = false;
  private disposed = false;

  constructor(options: LobbyOptions) {
    this.options = options;
    this.nameInput.value = options.initialName;
    this.codeInput.value = normalizeRoomCode(options.routeCode);
    this.root.hidden = false;
    this.root.setAttribute("aria-busy", "false");
    document.body.dataset.view = "lobby";
    this.createButton.addEventListener("click", this.handleCreate);
    this.aiButton.addEventListener("click", this.handleCreateAi);
    this.form.addEventListener("submit", this.handleJoin);
    this.nameInput.addEventListener("input", this.normalizeName);
    this.codeInput.addEventListener("input", this.normalizeCode);
    this.updateButtons();
  }

  setConnected(connected: boolean): void {
    this.connected = connected;
    if (connected && !this.busy) {
      this.setStatus("Ansluten", "ready");
    }
    this.updateButtons();
  }

  setBusy(busy: boolean): void {
    this.busy = busy;
    this.root.setAttribute("aria-busy", String(busy));
    this.updateButtons();
  }

  setStatus(message: string, tone: LobbyStatusTone = "normal"): void {
    this.statusMessage.textContent = message;
    this.status.dataset.tone = tone;
  }

  hide(): void {
    this.root.hidden = true;
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.createButton.removeEventListener("click", this.handleCreate);
    this.aiButton.removeEventListener("click", this.handleCreateAi);
    this.form.removeEventListener("submit", this.handleJoin);
    this.nameInput.removeEventListener("input", this.normalizeName);
    this.codeInput.removeEventListener("input", this.normalizeCode);
  }

  private getName(): string {
    return this.nameInput.value.trim() || "Skogsvän";
  }

  private updateButtons(): void {
    const actionsDisabled = !this.connected || this.busy;
    this.createButton.disabled = actionsDisabled;
    this.aiButton.disabled = actionsDisabled;
    this.joinButton.disabled = actionsDisabled || this.codeInput.value.length !== 5;
  }

  private readonly normalizeName = (): void => {
    const normalized = this.nameInput.value.replace(/[<>]/g, "").slice(0, 24);
    if (normalized !== this.nameInput.value) {
      this.nameInput.value = normalized;
    }
  };

  private readonly normalizeCode = (): void => {
    const normalized = normalizeRoomCode(this.codeInput.value);
    if (normalized !== this.codeInput.value) {
      this.codeInput.value = normalized;
    }
    this.updateButtons();
  };

  private readonly handleCreate = (): void => {
    if (!this.connected || this.busy) {
      return;
    }
    this.setBusy(true);
    this.options.onCreate(this.getName());
  };

  private readonly handleCreateAi = (): void => {
    if (!this.connected || this.busy) {
      return;
    }
    this.setBusy(true);
    this.options.onCreateAi(this.getName());
  };

  private readonly handleJoin = (event: SubmitEvent): void => {
    event.preventDefault();
    if (!this.connected || this.busy) {
      return;
    }
    const code = normalizeRoomCode(this.codeInput.value);
    this.codeInput.value = code;
    if (code.length !== 5) {
      this.setStatus("Matchkoden måste bestå av fem tecken.", "error");
      this.codeInput.focus();
      this.updateButtons();
      return;
    }
    this.setBusy(true);
    this.options.onJoin(this.getName(), code);
  };
}
